import React, { useState, useEffect, useMemo } from "react";
import QuestionImage from "./components/QuestionImage";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Download,
  FileQuestion,
  GraduationCap,
  Lightbulb,
  Mail,
  Medal,
  Sparkles,
  Target,
  Trophy,
  UserRound,
} from "lucide-react";
import { auth, db } from "./firebaseConfig";
import {
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { accountDisplayName, DashboardHeader, DashboardSidebar } from "./Dashboard";
import McqResultSummary from "./components/McqResultSummary";
import SubmittedDrawingAnswer from "./components/SubmittedDrawingAnswer";
import {
  attemptLabel,
  attemptProgression,
  buildAssessmentSeries,
  finalPolicyLabel,
  outcomeDisplayLabel,
  selectDetailedAttemptId,
} from "./utils/attemptResults";
import "./StudentProfile.css";
import { API_BASE_URL } from "./utils/apiBase";


const StudentProfile = ({ user }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  
  // State variables
  const [student, setStudent] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [assessmentSeries, setAssessmentSeries] = useState([]);
  const [selectedAttemptByQuiz, setSelectedAttemptByQuiz] = useState({});
  const [quizzes, setQuizzes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);
  const [extraTimeInput, setExtraTimeInput] = useState(0);
  const [updateSuccess, setUpdateSuccess] = useState(false); // For success message
  const [stats, setStats] = useState({
    totalQuizzes: 0,
    averageScore: 0,
    highestScore: 0,
    lowestScore: 100,
    totalSubmissions: 0,
    recentActivity: null,
  });
  const [expandedQuizCode, setExpandedQuizCode] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    // Get dark mode preference from localStorage or default to false
    const savedMode = localStorage.getItem("darkMode");
    return savedMode ? JSON.parse(savedMode) : false;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Check if user is logged in and is a teacher or admin
  useEffect(() => {
    if (!user) {
      navigate("/");
    } else if (user.role !== "teacher" && user.role !== "admin") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    const syncTheme = () => {
      const nextMode = localStorage.getItem("darkMode") === "true";
      setDarkMode(nextMode);
      document.body.classList.toggle("dark-mode", nextMode);
      document.documentElement.setAttribute("data-dark-mode", nextMode ? "true" : "false");
    };
    syncTheme();
    window.addEventListener("storage", syncTheme);
    return () => window.removeEventListener("storage", syncTheme);
  }, []);

  // Fetch student data with optimized batch queries
  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log(`[STUDENT PROFILE] Viewing student: ${studentId}`);
        console.log(`[STUDENT PROFILE] Current viewer role: ${user?.role}`);
        console.log(`[STUDENT PROFILE] Current teacher UID: ${user?.uid}`);

        // Fetch student profile
        const studentDocRef = doc(db, "users", studentId);
        const studentDoc = await getDoc(studentDocRef);
        
        if (!studentDoc.exists()) {
          throw new Error("Student profile not found");
        }
        
         const studentData = studentDoc.data();
         setStudent({
           id: studentDoc.id,
           displayName: studentData.displayName || studentData.username || "Unknown",
           email: studentData.email || "No email",
           createdAt: studentData.createdAt ? studentData.createdAt.toDate() : null,
           teacherCodes: studentData.teacherCodes || [],
           extraTimePercentage: studentData.extraTimePercentage || 0,
         });
        
        // Initialize the extra time input with the student's current value
        setExtraTimeInput(studentData.extraTimePercentage || 0);

        // Result projection and ownership filtering happen on the authenticated
        // server against authoritative question/quiz documents. The browser
        // receives display-safe MCQ label/text values, never option IDs.
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("You must be signed in to view student results.");
        const idToken = await currentUser.getIdToken();
        const resultResponse = await fetch(
          `${API_BASE_URL}/api/student-profile-results?studentId=${encodeURIComponent(studentId)}`,
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        const resultPayload = await resultResponse.json().catch(() => ({}));
        if (!resultResponse.ok) {
          throw new Error(resultPayload.error || "Student assessment results could not be loaded.");
        }
        const filteredSubmissionsData = Array.isArray(resultPayload.results)
          ? resultPayload.results.map((submission) => ({
              ...submission,
              submittedAt: submission.submittedAt ? new Date(submission.submittedAt) : new Date(),
            }))
          : [];

        // ── Rebuild filteredQuizzesData from filtered submissions ─────────────
        // Do NOT filter the original quizzesData map by quizCode — that map was
        // built from all submissions and still has the quizCode collision problem.
        // Instead, reconstruct quiz summaries from the already-filtered submissions.
        const series = buildAssessmentSeries({
          records: filteredSubmissionsData,
          finalResults: Array.isArray(resultPayload.finalResults) ? resultPayload.finalResults : [],
          attempts: Array.isArray(resultPayload.attempts) ? resultPayload.attempts : [],
          defaultStudentUid: studentId,
        });
        const selections = Object.fromEntries(series.map((item) => [
          item.quizId,
          selectDetailedAttemptId(item.attempts, item.finalResult),
        ]));
        const filteredQuizzesData = Object.fromEntries(series.map((item) => {
          const latestDate = item.attempts.map(({ submittedAt, startedAt }) => new Date(submittedAt || startedAt || 0))
            .reduce((latest, value) => value > latest ? value : latest, new Date(0));
          const summary = item.finalResult || item.summary;
          return [item.key, {
            key: item.key,
            code: item.records[0]?.quizCode || "NoCode",
            quizId: item.quizId || null,
            totalMarks: summary?.totalAwarded ?? null,
            maxMarks: summary?.totalPossible ?? null,
            percentage: Number.isFinite(summary?.percentage) ? summary.percentage : null,
            finalResult: item.finalResult,
            attempts: item.attempts,
            selectedAttemptId: selections[item.quizId],
            displayStatus: item.displayStatus,
            lastSubmitted: latestDate,
          }];
        }));
        const selectedSubmissions = series.flatMap((item) =>
          item.attempts.find(({ id }) => id === selections[item.quizId])?.records || []
        );

        setAssessmentSeries(series);
        setSelectedAttemptByQuiz(selections);
        setSubmissions(selectedSubmissions);
        setQuizzes(filteredQuizzesData);

        const canonicalPercentages = series
          .map(({ finalResult, summary }) => finalResult?.percentage ?? summary?.percentage)
          .filter(Number.isFinite);
        if (series.length > 0) {
          const mostRecentActivity = Object.values(filteredQuizzesData)
            .reduce((latest, quiz) => quiz.lastSubmitted > latest ? quiz.lastSubmitted : latest, new Date(0));
          setStats({
            totalQuizzes: series.length,
            averageScore: canonicalPercentages.length ? (canonicalPercentages.reduce((sum, value) => sum + value, 0) / canonicalPercentages.length).toFixed(2) : 0,
            highestScore: canonicalPercentages.length ? Math.max(...canonicalPercentages).toFixed(2) : 0,
            lowestScore: canonicalPercentages.length ? Math.min(...canonicalPercentages).toFixed(2) : 0,
            totalSubmissions: filteredSubmissionsData.length,
            recentActivity: mostRecentActivity,
          });
        } else {
          // Set empty statistics when no submissions match
          setStats({
            totalQuizzes: 0,
            averageScore: 0,
            highestScore: 0,
            lowestScore: 0,
            totalSubmissions: 0,
            recentActivity: null,
          });
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching student data:", error);
        setError(error.message || "Error fetching student data");
        setLoading(false);
      }
    };

    if (studentId) {
      fetchStudentData();
    }
  }, [studentId, user?.role, user?.uid]);

  // Handle updating extra time percentage
  const handleUpdateExtraTime = async (newExtraTime) => {
    try {
      await updateDoc(doc(db, "users", studentId), { extraTimePercentage: newExtraTime });
      setUpdateSuccess(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);
      
      // Update local state to reflect changes
      setStudent(prev => ({
        ...prev,
        extraTimePercentage: newExtraTime
      }));
    } catch (error) {
      console.error("Error updating extra time:", error.message);
      alert("Failed to update extra time. Please try again.");
    }
  };

  // Toggle expanded quiz
  const toggleExpand = (quizCode) => {
    setExpandedQuizCode(prev => prev === quizCode ? null : quizCode);
  };

  const selectProfileAttempt = (quizId, attemptId) => {
    const series = assessmentSeries.find((item) => item.quizId === quizId);
    if (!series?.attempts.some((attempt) => attempt.id === attemptId)) return;
    const selections = { ...selectedAttemptByQuiz, [quizId]: attemptId };
    setSelectedAttemptByQuiz(selections);
    setQuizzes((current) => Object.fromEntries(Object.entries(current).map(([key, quiz]) => [
      key,
      quiz.quizId === quizId ? { ...quiz, selectedAttemptId: attemptId } : quiz,
    ])));
    setSubmissions(assessmentSeries.flatMap((item) =>
      item.attempts.find(({ id }) => id === selections[item.quizId])?.records || []
    ));
  };

  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to get score class
  const getScoreClass = (percentage) => {
    if (percentage >= 80) return 'high';
    if (percentage >= 60) return 'medium';
    return 'low';
  };

  // Helper function to format feedback text with line breaks
  const formatFeedback = (feedback) => {
    if (feedback === "Pending") return feedback;
    
    // Format the feedback with better spacing
    const formattedFeedback = feedback
      .replace(/\.\s+/g, '.\n\n') // Add double line breaks after periods
      .replace(/:\s+/g, ':\n') // Add line breaks after colons
      .replace(/Score:\s+/g, '\nScore: '); // Ensure Score is on its own line
      
    return formattedFeedback;
  };

  // Extract common themes from AI feedback
  const extractInsights = (submissions) => {
    // Skip if no submissions or all are pending
    if (!submissions.length || submissions.every(s => s.feedback === "Pending")) {
      return { strengths: [], weaknesses: [], suggestions: [] };
    }
    
    // Simple analysis based on scores and feedback
    const strengths = [];
    const weaknesses = [];
    const suggestions = [];
    
    // Count high and low scoring submissions
    const highScoring = submissions.filter(s => {
      if (s.markAwarded === "Pending" || s.maxMark === 0) return false;
      return (parseFloat(s.markAwarded) / parseFloat(s.maxMark)) >= 0.8;
    });
    
    const lowScoring = submissions.filter(s => {
      if (s.markAwarded === "Pending" || s.maxMark === 0) return false;
      return (parseFloat(s.markAwarded) / parseFloat(s.maxMark)) < 0.6;
    });
    
    // Add insights based on score patterns
    if (highScoring.length > 0 && highScoring.length >= submissions.length * 0.3) {
      strengths.push("Consistently scores well on assessments");
    }
    
    if (lowScoring.length > 0 && lowScoring.length >= submissions.length * 0.3) {
      weaknesses.push("Struggles with some assessment questions");
      suggestions.push("Consider providing additional support materials");
    }
    
    // Look for keywords in feedback
    const allFeedback = submissions
      .filter(s => s.feedback !== "Pending")
      .map(s => s.feedback.toLowerCase());
    
    // Check for positive indicators
    if (allFeedback.some(f => f.includes("excellent") || f.includes("outstanding") || f.includes("very good"))) {
      strengths.push("Receives positive feedback on work quality");
    }
    
    // Check for improvement areas
    if (allFeedback.some(f => f.includes("improve") || f.includes("could have") || f.includes("should have"))) {
      suggestions.push("Review feedback for specific improvement areas");
    }
    
    // Check for understanding issues
    if (allFeedback.some(f => f.includes("misunderstood") || f.includes("confusion") || f.includes("unclear"))) {
      weaknesses.push("May have difficulty understanding some concepts");
      suggestions.push("Check for comprehension during lessons");
    }
    
    return {
      strengths: strengths.length ? strengths : ["Not enough data to determine strengths"],
      weaknesses: weaknesses.length ? weaknesses : ["Not enough data to determine areas for improvement"],
      suggestions: suggestions.length ? suggestions : ["Continue monitoring performance"],
    };
  };

  // Group submissions by quiz - memoized to prevent recalculation on every render
  const submissionsByQuiz = useMemo(() => {
    return submissions.reduce((acc, sub) => {
      const key = sub.quizId || sub.quizCode;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(sub);
      return acc;
    }, {});
  }, [submissions]);

  // Get insights from all submissions - memoized
  const insights = useMemo(() => extractInsights(submissions), [submissions]);

  // Generate CSV data for download
  const generateCSV = () => {
    if (Object.keys(quizzes).length === 0) {
      return "";
    }
    let csvContent = "Test Title,Percentage Score,Outcome,Attempts,Result Method,Status\n";
    Object.values(quizzes).forEach((quiz) => {
      const finalResult = quiz.finalResult;
      const values = [
        quiz.code,
        Number.isFinite(quiz.percentage) ? `${quiz.percentage}%` : "N/A",
        finalResult?.achievedOutcomeLabel || "",
        quiz.attempts.length,
        finalResult ? finalPolicyLabel(finalResult.policy) : "Score only",
        quiz.displayStatus,
      ].map((value) => `"${String(value).replace(/"/g, '""')}"`);
      csvContent += `${values.join(",")}\n`;
    });
    const percentages = Object.values(quizzes).map(({ percentage }) => percentage).filter(Number.isFinite);
    if (percentages.length) csvContent += `"Average Score","${(percentages.reduce((sum, value) => sum + value, 0) / percentages.length).toFixed(2)}%","","","",""\n`;
    return csvContent;
  };

  // Handle CSV download
  const handleDownloadCSV = () => {
    if (Object.keys(quizzes).length === 0) {
      alert("No assessment data available to download.");
      return;
    }
    
    const csvData = generateCSV();
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link and trigger download
    const link = document.createElement("a");
    const fileName = `${student.displayName.replace(/\s+/g, "_")}_assessments_${new Date().toISOString().split("T")[0]}.csv`;
    
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.display = "none";
    
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const toggleTheme = () => {
    const nextMode = !darkMode;
    localStorage.setItem("darkMode", String(nextMode));
    setDarkMode(nextMode);
    document.body.classList.toggle("dark-mode", nextMode);
    document.documentElement.setAttribute("data-dark-mode", nextMode ? "true" : "false");
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (logoutError) {
      console.error("Error signing out", logoutError);
    }
  };

  const handleCopyTeacherCode = async () => {
    if (!user?.teacherCode) return;
    try {
      await navigator.clipboard.writeText(user.teacherCode);
      setCopiedTeacherCode(true);
      window.setTimeout(() => setCopiedTeacherCode(false), 1800);
    } catch (copyError) {
      console.error("Could not copy teacher code", copyError);
    }
  };

  const username = accountDisplayName(user, user?.role === "admin" ? "Admin" : "Teacher");
  const visibleQuizzes = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return Object.values(quizzes);
    return Object.values(quizzes).filter((quiz) =>
      String(quiz.code || "").toLowerCase().includes(needle)
    );
  }, [quizzes, search]);

  return (
    <div className={"sd-dashboard sd-brand-theme sp-page" + (darkMode ? " sd-dark" : "")}>
      <DashboardSidebar
        role={user?.role || "teacher"}
        username={username}
        creditsRemaining={user?.role === "admin" ? "unlimited" : user?.creditsRemaining ?? null}
        teacherCode={user?.teacherCode || ""}
        onCopyCode={handleCopyTeacherCode}
        copied={copiedTeacherCode}
        onLogout={handleLogout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePath="/view-students"
        officialDashboardBrand
      />

      <div className="sd-workspace sp-workspace">
        <DashboardHeader
          username={username}
          role={user?.role || "teacher"}
          darkMode={darkMode}
          onToggleTheme={toggleTheme}
          onOpenMenu={() => setSidebarOpen(true)}
          search={search}
          onSearch={setSearch}
        />

        <main className="sd-main sp-main">
          <section className="sp-hero">
            <div className="sp-hero-copy">
              <button type="button" className="sp-back-button" onClick={() => navigate(-1)}>
                <ChevronLeft size={15} />Classes &amp; Students
              </button>
              <p className="sp-kicker">Student profile</p>
              <h1>{student?.displayName || "Student overview"}</h1>
              <p>Student performance and account overview</p>
            </div>
            <div className="sp-hero-identity">
              <span className="sp-hero-avatar">{student?.displayName?.charAt(0).toUpperCase() || <UserRound size={28} />}</span>
              <div>
                <strong>{student?.displayName || "Loading student..."}</strong>
                <span><UserRound size={13} />Student</span>
                <small><CalendarDays size={13} />Joined {student?.createdAt ? formatDate(student.createdAt) : "date unavailable"}</small>
              </div>
              <i className="sp-orbit sp-orbit-one"><Sparkles size={16} /></i>
              <i className="sp-orbit sp-orbit-two"><GraduationCap size={17} /></i>
            </div>
          </section>

          {loading && (
            <section className="sp-state" role="status">
              <div className="sp-spinner" aria-hidden="true" />
              <h2>Loading student profile...</h2>
              <p>Gathering account and assessment information.</p>
            </section>
          )}

          {!loading && error && (
            <section className="sp-state sp-state-error" role="alert">
              <AlertTriangle size={31} />
              <h2>We could not load this student</h2>
              <p>{error}</p>
              <button type="button" onClick={() => navigate("/view-students")}>
                <ChevronLeft size={16} />Back to Classes &amp; Students
              </button>
            </section>
          )}

          {!loading && !error && student && (
            <>
              <section className="sp-profile-grid">
                <article className="sp-card sp-identity-card">
                  <div className="sp-card-heading">
                    <span className="sp-heading-icon sp-heading-icon-blue"><UserRound size={21} /></span>
                    <div><p>Account details</p><h2>Student Identity</h2></div>
                  </div>
                  <div className="sp-identity-body">
                    <span className="sp-profile-avatar">{student.displayName.charAt(0).toUpperCase()}</span>
                    <div className="sp-identity-details">
                      <h3>{student.displayName}</h3>
                      <p><Mail size={15} />{student.email}</p>
                      <p><CalendarDays size={15} />Joined {student.createdAt ? formatDate(student.createdAt) : "Unknown"}</p>
                    </div>
                    <span className="sp-student-badge"><GraduationCap size={13} />Student</span>
                  </div>
                </article>

                {(user?.role === "teacher" || user?.role === "admin") && (
                  <article className="sp-card sp-accommodation-card">
                    <div className="sp-card-heading">
                      <span className="sp-heading-icon sp-heading-icon-amber"><Clock3 size={21} /></span>
                      <div><p>Assessment accommodation</p><h2>Extra Time Allowance</h2></div>
                    </div>
                    <p className="sp-card-description">
                      Specify additional time this student should receive on timed assessments.
                    </p>
                    <div className="sp-time-controls">
                      <label className="sp-time-input">
                        <input
                          type="number"
                          min="0"
                          max="200"
                          value={extraTimeInput}
                          onChange={(event) => setExtraTimeInput(Number(event.target.value))}
                          aria-label="Extra time percentage"
                        />
                        <span>%</span>
                      </label>
                      <button type="button" onClick={() => handleUpdateExtraTime(Number(extraTimeInput))}>
                        <CheckCircle2 size={16} />Update Time
                      </button>
                    </div>
                    {updateSuccess && (
                      <div className="sp-success-message" role="status">
                        <CheckCircle2 size={16} />Extra time updated successfully!
                      </div>
                    )}
                  </article>
                )}
              </section>

              <section className="sp-card sp-performance-panel">
                <div className="sp-section-header">
                  <div className="sp-card-heading">
                    <span className="sp-heading-icon sp-heading-icon-purple"><BarChart3 size={21} /></span>
                    <div><p>Student analytics</p><h2>Performance Summary</h2></div>
                  </div>
                  <button type="button" className="sp-download-button" onClick={handleDownloadCSV}
                    title="Download assessment data as CSV">
                    <Download size={16} />Download Data
                  </button>
                </div>

                <div className="sp-metrics">
                  <article className="sp-metric sp-metric-blue">
                    <span><Trophy size={21} /></span>
                    <div><strong>{stats.totalQuizzes}</strong><h3>Tests Taken</h3><p>Completed test groups</p></div>
                  </article>
                  <article className="sp-metric sp-metric-purple">
                    <span><Target size={21} /></span>
                    <div><strong>{stats.averageScore}%</strong><h3>Average Score</h3><p>Across available marks</p></div>
                  </article>
                  <article className="sp-metric sp-metric-green">
                    <span><Medal size={21} /></span>
                    <div><strong>{stats.highestScore}%</strong><h3>Highest Score</h3><p>Best test performance</p></div>
                  </article>
                  <article className="sp-metric sp-metric-teal">
                    <span><FileQuestion size={21} /></span>
                    <div><strong>{stats.totalSubmissions}</strong><h3>Total Submissions</h3><p>Question responses</p></div>
                  </article>
                </div>
              </section>

              <section className="sp-card sp-insights-panel">
                <div className="sp-card-heading">
                  <span className="sp-heading-icon sp-heading-icon-green"><Lightbulb size={21} /></span>
                  <div><p>Existing feedback signals</p><h2>AI Analysis Insights</h2></div>
                </div>
                {submissions.length > 0 ? (
                  <div className="sp-insights-grid">
                    <article className="sp-insight sp-insight-strength">
                      <h3>Strengths</h3>
                      <ul>{insights.strengths.map((strength, index) => <li key={index}>{strength}</li>)}</ul>
                    </article>
                    <article className="sp-insight sp-insight-improve">
                      <h3>Areas for Improvement</h3>
                      <ul>{insights.weaknesses.map((weakness, index) => <li key={index}>{weakness}</li>)}</ul>
                    </article>
                    <article className="sp-insight sp-insight-suggestion">
                      <h3>Teaching Suggestions</h3>
                      <ul>{insights.suggestions.map((suggestion, index) => <li key={index}>{suggestion}</li>)}</ul>
                    </article>
                  </div>
                ) : (
                  <div className="sp-empty"><Lightbulb size={26} /><p>No data available for AI analysis yet.</p></div>
                )}
              </section>

              <section className="sp-card sp-history-panel">
                <div className="sp-section-header">
                  <div className="sp-card-heading">
                    <span className="sp-heading-icon sp-heading-icon-teal"><FileQuestion size={21} /></span>
                    <div><p>Assessment results</p><h2>Test History</h2></div>
                  </div>
                  <span className="sp-history-count">{visibleQuizzes.length} shown</span>
                </div>

                {Object.keys(quizzes).length === 0 ? (
                  <div className="sp-empty"><FileQuestion size={27} /><p>This student has not completed any tests for your class yet.</p></div>
                ) : visibleQuizzes.length === 0 ? (
                  <div className="sp-empty"><FileQuestion size={27} /><p>No test history matches your search.</p></div>
                ) : (
                  <div className="sp-quiz-list">
                    {visibleQuizzes.map((quiz, index) => {
                      const percentage = Number.isFinite(quiz.percentage)
                        ? Number(quiz.percentage).toFixed(2)
                        : "N/A";
                      const scoreClass = getScoreClass(parseFloat(percentage));
                      const isExpanded = expandedQuizCode === quiz.key;
                      const selectedAttempt = quiz.attempts.find(({ id }) => id === quiz.selectedAttemptId) || null;
                      const progression = attemptProgression(quiz.attempts);
                      const quizResultsIdentifier = quiz.quizId || (quiz.code && quiz.code !== "NoCode" ? quiz.code : "");
                      const quizResultsPath = quizResultsIdentifier
                        ? "/quiz-results/" + encodeURIComponent(quizResultsIdentifier)
                        : null;

                      return (
                        <article key={quiz.key} className={"sp-quiz-card sp-tone-" + (index % 4)}>
                          <div className="sp-quiz-header">
                            <span className="sp-quiz-icon"><FileQuestion size={20} /></span>
                            <div className="sp-quiz-info">
                              <h3>
                                {quizResultsPath
                                  ? <Link to={quizResultsPath}>{quiz.code}</Link>
                                  : quiz.code}
                              </h3>
                              <p><CalendarDays size={13} />Last submission: {formatDate(quiz.lastSubmitted)}</p>
                            </div>
                            <div className="sp-quiz-score-group">
                              <span className={"sp-quiz-score " + scoreClass}>
                                {percentage}%
                                {Number.isFinite(quiz.totalMarks) && Number.isFinite(quiz.maxMarks) && ` · ${quiz.totalMarks}/${quiz.maxMarks}`}
                              </span>
                              {quiz.finalResult?.achievedOutcomeLabel && <span className="sp-outcome-chip">{outcomeDisplayLabel(quiz.finalResult.achievedOutcomeLabel)}</span>}
                              <small>{quiz.attempts.length} attempt{quiz.attempts.length === 1 ? "" : "s"} · {quiz.finalResult ? finalPolicyLabel(quiz.finalResult.policy) : "Score only"}</small>
                              <button type="button" onClick={() => toggleExpand(quiz.key)}
                                aria-expanded={isExpanded} aria-label={(isExpanded ? "Hide" : "Show") + " test submission details"}>
                                {isExpanded ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                                {isExpanded ? "Hide details" : "View details"}
                              </button>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="sp-quiz-details">
                              <div className="sp-attempt-context">
                                <div className="sp-attempt-list" aria-label={`Attempt history for ${quiz.code}`}>
                                  {quiz.attempts.map((attempt) => {
                                    const complete = attempt.outcome?.status === "complete";
                                    return <button key={attempt.id} type="button"
                                      className={attempt.id === quiz.selectedAttemptId ? "selected" : ""}
                                      aria-pressed={attempt.id === quiz.selectedAttemptId}
                                      onClick={() => selectProfileAttempt(quiz.quizId, attempt.id)}>
                                      <strong>{attemptLabel(attempt.attemptNumber)}</strong>
                                      <span>{attempt.status === "in_progress" ? "In progress" : complete ? `${attempt.outcome.percentage}%` : "Marking in progress"}</span>
                                      {complete && attempt.outcome.achievedOutcomeLabel && <small>{outcomeDisplayLabel(attempt.outcome.achievedOutcomeLabel)}</small>}
                                    </button>;
                                  })}
                                </div>
                                <p><b>Viewing {selectedAttempt ? attemptLabel(selectedAttempt.attemptNumber) : "attempt"}</b>{progression ? ` · Change from Attempt 1: ${progression.label}` : ""}</p>
                              </div>
                              <div className="sp-table-scroll">
                                <table className="sp-submissions-table">
                                  <thead><tr><th>Question</th><th>Answer</th><th>Feedback</th><th>Score</th></tr></thead>
                                  <tbody>
                                    {(submissionsByQuiz[quiz.quizId] || []).map((sub) => (
                                      <tr key={sub.id}>
                                        <td className="sp-question-cell">
                                          <div>{sub.questionText}</div>
                                          {(sub.questionImageURL || sub.questionMedia) && <QuestionImage question={sub}
                                            quizId={quiz.quizId} context={{ type: "owner" }} alt="Question" />}
                                        </td>
                                        <td className="sp-answer-cell">
                                          {sub.questionType === "multipleChoice" && sub.mcqResult ? (
                                            <McqResultSummary
                                              mcqResult={sub.mcqResult}
                                              showCorrect
                                              studentLabel="Student answer"
                                            />
                                          ) : (
                                            <>
                                              {sub.userAnswer}
                                              <SubmittedDrawingAnswer result={sub} questionText={sub.questionText} typedAnswer={sub.userAnswer}
                                                mark={sub.markAwarded} maxMark={sub.maxMark} feedback={sub.feedback} />
                                            </>
                                          )}
                                        </td>
                                        <td className="sp-feedback-cell">
                                          {sub.feedback !== "Pending" ? <div>{formatFeedback(sub.feedback)}</div> : "Pending"}
                                        </td>
                                        <td className="sp-score-cell">
                                          {sub.markAwarded !== "Pending" ? (
                                            <span className={"sp-score-badge " + (
                                              parseFloat(sub.markAwarded) === parseFloat(sub.maxMark)
                                                ? "full"
                                                : parseFloat(sub.markAwarded) > 0 ? "partial" : "zero"
                                            )}>
                                              {sub.markAwarded}/{sub.maxMark}
                                            </span>
                                          ) : <span className="sp-score-badge pending">Pending</span>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {(submissionsByQuiz[quiz.quizId] || []).length === 0 && <p className="sp-attempt-empty">No completed responses are available for this attempt.</p>}
                              </div>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </main>

        <footer className="sp-footer">© {new Date().getFullYear()} StudyDen. All rights reserved.</footer>
      </div>
    </div>
  );
};

export default StudentProfile;
